import { FormEvent, useEffect, useMemo, useState } from "react";
import { IEventApcap } from "../../@types/system";
import { formatDate } from "../../utils/helpers";
import api from "../../service/api";
import { AlertCircle, CheckCircle2, Info } from "lucide-react";

type CheckinFeedbackType = "success" | "info" | "error";

interface CheckinFeedback {
  type: CheckinFeedbackType;
  message: string;
}

/**
 * Converte string de data (ex: "2025-03-14" ou ISO) em Date no fuso local.
 * Evita que "YYYY-MM-DD" seja interpretado como meia-noite UTC (que vira dia anterior no Brasil).
 */
function parseDateAsLocal(dateStr: string): Date | null {
  const dateOnly = dateStr.split("T")[0];
  const parts = dateOnly.split("-");
  if (parts.length !== 3) return null;
  const year = Number(parts[0]);
  const month = Number(parts[1]) - 1;
  const day = Number(parts[2]);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  return new Date(year, month, day);
}

function isEventInProgress(initialDate: string, finishDate: string) {
  const now = new Date();
  const start = parseDateAsLocal(initialDate);
  const end = parseDateAsLocal(finishDate);

  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return false;
  }

  const endOfDay = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);

  return now >= start && now <= endOfDay;
}

function formatCpf(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  }

  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(
    6,
    9
  )}-${digits.slice(9)}`;
}

export default function CheckinPublico() {
  const [events, setEvents] = useState<IEventApcap[]>([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [cpf, setCpf] = useState("");
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<CheckinFeedback | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  useEffect(() => {
    async function loadEvents() {
      setLoadingEvents(true);
      try {
        const { data } = await api.get<IEventApcap[]>("/events");
        const eventsWithOpenSubscription = data.filter(
          (event) =>
            event.enable_subscription &&
            isEventInProgress(event.initial_date, event.finish_date)
        );

        setEvents(eventsWithOpenSubscription);

        setSelectedEventId((currentSelectedEventId) => {
          if (eventsWithOpenSubscription.length === 0) {
            return "";
          }

          const stillExists = eventsWithOpenSubscription.some(
            (event) => event.id === currentSelectedEventId
          );

          return stillExists
            ? currentSelectedEventId
            : eventsWithOpenSubscription[0].id;
        });
      } finally {
        setLoadingEvents(false);
      }
    }

    loadEvents();
  }, []);

  const selectedEvent = useMemo(
    () => events.find((event) => event.id === selectedEventId),
    [events, selectedEventId]
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const cpfDigits = cpf.replace(/\D/g, "");

    if (!selectedEventId) {
      setFeedback({
        type: "error",
        message: "Selecione um evento para continuar.",
      });
      return;
    }

    if (cpfDigits.length !== 11) {
      setFeedback({
        type: "error",
        message: "Informe um CPF valido com 11 digitos.",
      });
      return;
    }

    setSubmitting(true);
    setFeedback(null);
    setShowFeedbackModal(false);

    try {
      const { status, data } = await api.post("events/accreditation/checkin", {
        event_id: selectedEventId,
        document: cpfDigits,
        cpf: cpfDigits,
      });

      if (status === 201) {
        setFeedback({
          type: "success",
          message: "Check-in realizado com sucesso.",
        });
        setShowFeedbackModal(true);
        setCpf("");
        return;
      }

      setFeedback({
        type: "info",
        message: data?.message || "Voce ja realizou o check-in hoje.",
      });
      setShowFeedbackModal(true);
    } catch (error: unknown) {
      const apiMessage: string =
        (
          error as { response?: { data?: { message?: string } } } | undefined
        )?.response?.data?.message || "Erro ao realizar check-in.";

      if (apiMessage.toLowerCase().includes("não encontrado")) {
        setFeedback({
          type: "error",
          message: "Usuário não encontrado neste evento.",
        });
      } else if (apiMessage.toLowerCase().includes("dados incompletos")) {
        setFeedback({
          type: "error",
          message:
            "A API de check-in ainda não aceita CPF nesta rota. Atualize/deploy da API para a versão nova.",
        });
      } else {
        setFeedback({
          type: "error",
          message: apiMessage,
        });
      }
      setShowFeedbackModal(true);
    } finally {
      setSubmitting(false);
    }
  }

  const feedbackStyles = useMemo(() => {
    if (!feedback) {
      return {
        icon: <Info className="h-16 w-16 text-blue-500" strokeWidth={2.5} />,
        title: "Informação",
        accent: "from-blue-500/20 to-cyan-500/20",
        badge: "bg-blue-100 text-blue-700",
      };
    }

    if (feedback.type === "success") {
      return {
        icon: (
          <CheckCircle2 className="h-16 w-16 text-emerald-500" strokeWidth={2.5} />
        ),
        title: "Check-in confirmado",
        accent: "from-emerald-500/20 to-green-500/20",
        badge: "bg-emerald-100 text-emerald-700",
      };
    }

    if (feedback.type === "info") {
      return {
        icon: <Info className="h-16 w-16 text-blue-500" strokeWidth={2.5} />,
        title: "Atenção",
        accent: "from-blue-500/20 to-cyan-500/20",
        badge: "bg-blue-100 text-blue-700",
      };
    }

    return {
      icon: <AlertCircle className="h-16 w-16 text-red-500" strokeWidth={2.5} />,
      title: "Não foi possível concluir",
      accent: "from-red-500/20 to-orange-500/20",
      badge: "bg-red-100 text-red-700",
    };
  }, [feedback]);

  return (
    <main className="min-h-screen bg-[radial-gradient(circle,_#015832_20%,_#003b43)] px-4 py-10 text-zinc-100">
      <div className="mx-auto w-full max-w-4xl">
        <header className="mb-6 text-center">
          <h1 className="text-3xl font-bold">Check-in publico</h1>
          <p className="mt-2 text-sm text-zinc-200">
            Selecione o evento com inscrição aberta e informe seu CPF.
          </p>
        </header>

        {loadingEvents && (
          <div className="rounded-md bg-white/10 p-4 text-center">
            Carregando eventos...
          </div>
        )}

        {!loadingEvents && events.length === 0 && (
          <div className="rounded-md bg-white/10 p-4 text-center">
            Nenhum evento com inscrição aberta dentro do período de realização.
          </div>
        )}

        {!loadingEvents && events.length > 0 && (
          <>
            <section className="grid gap-5 md:grid-cols-2">
              {events.map((event) => {
                const isSelected = selectedEventId === event.id;

                return (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => setSelectedEventId(event.id)}
                    className={`group relative overflow-hidden rounded-2xl border text-left shadow-lg transition-all duration-200 ${isSelected
                      ? "border-emerald-300 bg-emerald-950/40 ring-2 ring-emerald-300/70"
                      : "border-white/15 bg-white/10 hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/15"
                      }`}
                  >
                    <div className="absolute right-3 top-3 z-10 rounded-full border border-white/30 bg-black/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-100">
                      {isSelected ? "Selecionado" : "Disponivel"}
                    </div>

                    <div className="relative h-36 w-full overflow-hidden border-b border-white/10">
                      <img
                        src={event.banner_url}
                        alt={event.name}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent" />
                    </div>

                    <div className="p-4">
                      <h2 className="line-clamp-2 text-lg font-bold leading-tight">
                        {event.name}
                      </h2>
                      <p className="mt-2 line-clamp-2 text-sm text-zinc-200">
                        {event.description}
                      </p>
                      <div className="mt-4 rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-zinc-300">
                          Período do evento
                        </p>
                        <p className="mt-1 text-sm font-medium text-zinc-100">
                          {formatDate(event.initial_date)} ate{" "}
                          {formatDate(event.finish_date)}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </section>

            <form
              onSubmit={handleSubmit}
              className="mt-6 rounded-lg border border-white/20 bg-white/10 p-5"
            >
              <div className="mb-4">
                <p className="text-sm text-zinc-200">Evento selecionado</p>
                <p className="font-semibold">
                  {selectedEvent?.name || "Nenhum evento selecionado"}
                </p>
              </div>

              <label htmlFor="cpf" className="mb-2 block text-sm font-medium">
                CPF
              </label>
              <input
                id="cpf"
                type="text"
                value={cpf}
                onChange={(event) => setCpf(formatCpf(event.target.value))}
                placeholder="000.000.000-00"
                className="w-full rounded-md border border-white/30 bg-white/90 px-3 py-2 text-zinc-900"
              />

              <button
                type="submit"
                disabled={submitting}
                className="mt-4 w-full rounded-md bg-green-600 px-4 py-2 font-semibold text-white transition hover:bg-green-700 disabled:opacity-60"
              >
                {submitting ? "Enviando..." : "Realizar check-in"}
              </button>
            </form>
          </>
        )}

        {showFeedbackModal && feedback && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
            <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white text-zinc-900 shadow-2xl">
              <div
                className={`bg-gradient-to-r ${feedbackStyles.accent} px-6 pb-4 pt-6`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    {feedbackStyles.icon}
                    <div>
                      <p
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${feedbackStyles.badge}`}
                      >
                        Resultado do check-in
                      </p>
                      <h2 className="mt-2 text-xl font-bold">{feedbackStyles.title}</h2>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowFeedbackModal(false)}
                    className="rounded-md bg-white/70 px-2 py-1 text-sm font-medium text-zinc-700 transition hover:bg-white"
                  >
                    Fechar
                  </button>
                </div>
              </div>

              <div className="px-6 pb-6 pt-4">
                <p className="text-base leading-relaxed text-zinc-700">
                  {feedback.message}
                </p>

                {selectedEvent && (
                  <div className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                    <p className="text-xs uppercase tracking-wide text-zinc-500">
                      Evento
                    </p>
                    <p className="text-sm font-semibold text-zinc-800">
                      {selectedEvent.name}
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setShowFeedbackModal(false)}
                  className="mt-5 w-full rounded-md bg-zinc-900 px-4 py-2 font-semibold text-white transition hover:bg-zinc-700"
                >
                  Entendi
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
